import { Component, ErrorInfo, ReactNode } from 'react';
import { Text, Layout, Spacer } from '@visiba-cortex/react-ui-std';
import { Logger } from '@modules/logger/logger';

interface Props {
	children?: ReactNode;
	fallback?: JSX.Element;
}

interface State {
	error: Error | null;
	errorInfo: ErrorInfo | null;
	fallback: JSX.Element | null;
	hasError: boolean;
}

function NonRecoverableError({ error, errorInfo }: { error: Error | null; errorInfo: ErrorInfo | null }): JSX.Element {
	return (
		<Layout grow>
			<Text.p>
				Something went wrong
				<span role='img' aria-label='test'>
					🤦‍♂️
				</span>
			</Text.p>
			{process.env.NODE_ENV === 'development' && (
				<details style={{ whiteSpace: 'pre-wrap' }}>
					{error && error.toString()}
					<Spacer height={20} />
					{errorInfo?.componentStack}
				</details>
			)}
		</Layout>
	);
}

// ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️
//
// It's good to know that the error overlay will still show during development
// and to this day there's not good way to circumvent it.
// See: https://github.com/facebook/create-react-app/issues/6530
//
// ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️

export class ErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props);

		this.state = {
			error: null,
			errorInfo: null,
			fallback: props.fallback ?? null,
			hasError: false,
		};
	}

	public static getDerivedStateFromError(error: unknown): { hasError: boolean; error: unknown } {
		// Update state so the next render will show the fallback UI.
		return { hasError: true, error };
	}

	componentDidCatch(_error: unknown, errorInfo: ErrorInfo): void {
		if (_error instanceof Promise) {
			Logger.assert(Logger.Level.error, `a promise was thrown, was the component not wrapped in suspense?`);

			return;
		}

		let error: Error;
		let message: string;
		if (_error instanceof Error) {
			error = _error;
			message = _error.message;
		} else {
			message = `${_error}`;
			error = new Error(message);
		}

		Logger.assert(Logger.Level.error, message, {
			stackTrace: errorInfo.componentStack,
			data: {},
			tags: [],
			immediately: true,
		});

		this.setState({
			errorInfo,
			error,
		});
	}

	render(): ReactNode {
		const { fallback, errorInfo, error, hasError } = this.state;

		if (hasError && fallback === null) {
			return <NonRecoverableError error={error} errorInfo={errorInfo} />;
		} else if (hasError && fallback !== null) {
			return fallback;
		}

		return this.props.children;
	}
}
